import { TrashIcon } from '@heroicons/react/24/solid'
import { Element, Type } from '../../../../../types/Survey/Element'
import StaticText from './Element/StaticText'
import SliderGroup from './Element/SliderGroup'
import DNDList from '../../../../UI/DNDList/DNDList'
import props from '../../../../../redux/props'
import { useSelector } from 'react-redux'
import Slider from './Element/Slider'
import Selection from './Element/Selection'
import DynamicText from './Element/DynamicText'
import RemoveConfirmation from '../../../../UI/RemoveConfirmation/RemoveConfirmation'
import DaySelection from './Element/DaySelection'
import OrganizationUnitSelection from './Element/OrganizationUnitSelection'
import OrganizationUnitCooperationSelection from './Element/OrganizationUnitCooperationSelection'
import SliderDropdown from './Element/SliderDropdown'
import SliderDropdownGroup from './Element/SliderDropdownGroup'
import BooleanSelection from './Element/BooleanSelection'
import NumberInput from './Element/NumberInput'
import DynamicNumberDescriptionAmountInput from './Element/DynamicNumberDescriptionAmountInput'
import AmountToggleInput from './Element/AmountToggleInput'
import OrganizationUnitNumberInput from './Element/OrganizationUnitNumberInput'
import DynamicOptionsWithNumberAndSelection from './Element/DynamicOptionsWithNumberAndSelection'

const ElementList = ({ survey, update }) => {
  const t = useSelector((s) => s[props.TRANSLATION])

  const remove = (uuid: string) => () => {
    const newElements = survey.elements.filter((e) => e.uuid !== uuid)
    update({ ...survey, elements: newElements })
  }

  const updateElement = (uuid: string) => (element: Element) => {
    const newElements = survey.elements.map((e) => (e.uuid === uuid ? element : e))
    update({ ...survey, elements: newElements })
  }

  const updateElementOrder = (elements: Element[]) => update({ ...survey, elements })

  const typesExcludedFromRemoval = [Type.DAY_SELECTION, Type.ORGANIZATION_UNIT_SELECTION]

  return (
    <div className="flex flex-col">
      <DNDList
        orderUpdateCallback={updateElementOrder}
        items={survey.elements.map((element: Element, index: number) => {
          return {
            id: element.uuid,
            data: element,
            content: (
              <div key={element.uuid} className="flex flex-row items-start border-b border-cyan border-opacity-20 px-5 py-5 gap-3 bg-white">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 aspect-square bg-cyan rounded-full flex items-center justify-center text-white">{index + 1}</div>
                  {!typesExcludedFromRemoval.includes(element.type) && (
                    <RemoveConfirmation itemName={element.title || ''} confirmationCallback={remove(element.uuid)}>
                      <TrashIcon className="fill-blue opacity-40 hover:opacity-100 transition-all w-6 cursor-pointer" />
                    </RemoveConfirmation>
                  )}
                </div>
                <div className="flex flex-col w-full">
                  <div className="text-gray text-sm mb-1 font-bold">{t.survey.elements.types[element.prebuildType || element.type]}</div>
                  <div className="bg-lightGray shadow-inner p-5">
                    {element.type === Type.DAY_SELECTION && <DaySelection survey={survey} element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.ORGANIZATION_UNIT_SELECTION && (
                      <OrganizationUnitSelection survey={survey} element={element} update={updateElement(element.uuid)} />
                    )}
                    {element.type === Type.ORGANIZATION_UNIT_COOPERATION_SELECTION && (
                      <OrganizationUnitCooperationSelection survey={survey} element={element} update={updateElement(element.uuid)} />
                    )}
                    {element.type === Type.STATIC_TEXT && <StaticText element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.SLIDER_GROUP && <SliderGroup element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.SLIDER && <Slider element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.SELECTION && <Selection element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.DYNAMIC_TEXT && <DynamicText element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.SLIDER_DROPDOWN && <SliderDropdown element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.SLIDER_DROPDOWN_GROUP && <SliderDropdownGroup element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.BOOLEAN_SELECTION && <BooleanSelection element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.NUMBER_INPUT && <NumberInput element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.DYNAMIC_NUMBER_DESCRIPTION_AMOUNT_INPUT && (
                      <DynamicNumberDescriptionAmountInput element={element} update={updateElement(element.uuid)} />
                    )}
                    {element.type === Type.AMOUNT_TOGGLE_INPUT && <AmountToggleInput element={element} update={updateElement(element.uuid)} />}
                    {element.type === Type.ORGANIZATION_UNIT_NUMBER_INPUT && (
                      <OrganizationUnitNumberInput survey={survey} element={element} update={updateElement(element.uuid)} />
                    )}
                    {element.type === Type.DYNAMIC_OPTIONS_WITH_NUMBER_AND_SELECTION && (
                      <DynamicOptionsWithNumberAndSelection element={element} update={updateElement(element.uuid)} />
                    )}
                  </div>
                </div>
              </div>
            ),
          }
        })}
      />
    </div>
  )
}

export default ElementList
